import { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtBRL, fmtDate, STATUS } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ClientModal } from "@/components/client/ClientModal";
import { MacLookupBox } from "@/components/clients/MacLookupBox";
import { TrialButton } from "@/components/TrialDialog";
import { NewClientDialog } from "@/components/client/NewClientDialog";
import { toast } from "sonner";
import {
  Search, Plus, MessageCircle, Mail, Send, X, Trash2, ChevronLeft, ChevronRight, Phone, Eye, EyeOff, Copy, ClipboardList, ListVideo } from "lucide-react";
import { copyInfo, copyM3U } from "@/lib/clientCopy";

const statusOptions = [
  ["", "Todos os status"], ["ativo", "Ativo"], ["a_vencer", "A Vencer"],
  ["vencido", "Vencido"], ["teste", "Teste"], ["sem_vencimento", "Sem vencimento"], ["desativado", "Desativado"],
];

export default function Clients() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [account, setAccount] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const perPage = 20;
  const reqId = useRef(0);

  useEffect(() => { api.get("/filters").then((r) => setAccounts(r.data.accounts || [])); }, []);

  const load = useCallback(() => {
    const rid = (reqId.current += 1);
    api.get("/clients", { params: { search, status, account, page, per_page: perPage, sort: "due_date" } })
      .then((r) => { if (rid === reqId.current) setData(r.data); });
  }, [search, status, account, page]);
  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(data.total / perPage));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-100">Clientes</h1>
          <p className="text-sm text-slate-400 mt-1">{data.total.toLocaleString("pt-BR")} clientes</p>
        </div>
        <div className="flex items-center gap-2">
        <TrialButton onCreated={() => load()} />
        <button data-testid="add-client-button" onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 transition-colors">
          <Plus className="h-4 w-4" /> Adicionar
        </button>
        </div>
      </div>

      <MacLookupBox onOpen={setOpenId} />

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input data-testid="client-search-input" value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Buscar por nome, usuário, e-mail ou WhatsApp..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[#111827] border border-[#1F293D] text-sm text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none" />
        </div>
        <select data-testid="client-status-filter" value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="px-4 py-2.5 rounded-lg bg-[#111827] border border-[#1F293D] text-sm text-slate-200 focus:border-sky-500 focus:outline-none">
          {statusOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {accounts.length > 0 && (
          <select data-testid="client-account-filter" value={account}
            onChange={(e) => { setAccount(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-lg bg-[#111827] border border-[#1F293D] text-sm text-slate-200 focus:border-sky-500 focus:outline-none">
            <option value="">Todas as contas</option>
            {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
      </div>

      <div className="bg-[#111827] border border-[#1F293D] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-[#1F293D]">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium">Valor</th>
                <th className="px-4 py-3 font-medium">Vencimento</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`}
                  className="border-b border-[#1F293D] hover:bg-[#151e30] transition-colors cursor-pointer"
                  onClick={() => setOpenId(c.id)}>
                  <td className="px-4 py-3">
                    <div className="text-slate-100 font-medium">{c.name}</div>
                    <div className="font-mono text-xs text-slate-500">{c.panel_user}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{c.plan || "—"}</td>
                  <td className="px-4 py-3 font-mono text-slate-200">{fmtBRL(c.value)}</td>
                  <td className="px-4 py-3 font-mono text-slate-300">{fmtDate(c.due_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.billing_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                      {c.whatsapp && (
                        <a target="_blank" rel="noreferrer" title="WhatsApp"
                          href={`https://wa.me/${c.whatsapp}`}
                          className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"><MessageCircle className="h-4 w-4" /></a>
                      )}
                      <button data-testid={`row-copy-info-${c.id}`} title="Copiar informações" onClick={() => copyInfo(c)}
                        className="p-2 rounded-lg bg-sky-500/10 text-sky-300 hover:bg-sky-500/20"><ClipboardList className="h-4 w-4" /></button>
                      <button data-testid={`row-copy-m3u-${c.id}`} title="Copiar M3U" disabled={!c.panel_user || !c.panel_password} onClick={() => copyM3U(c)}
                        className="p-2 rounded-lg bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 disabled:opacity-40"><ListVideo className="h-4 w-4" /></button>
                      <button title="Abrir" onClick={() => setOpenId(c.id)}
                        className="p-2 rounded-lg bg-sky-500/10 text-sky-400 hover:bg-sky-500/20"><Send className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Nenhum cliente encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#1F293D]">
          <span className="text-xs text-slate-500">Página {page} de {pages}</span>
          <div className="flex gap-2">
            <button data-testid="prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
              className="p-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 disabled:opacity-40 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
            <button data-testid="next-page" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}
              className="p-2 rounded-lg bg-[#1A2234] border border-[#202D45] text-slate-300 disabled:opacity-40 hover:text-white"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <ClientModal id={openId} onClose={() => { setOpenId(null); load(); }} />
      {showAdd && <NewClientDialog onClose={() => { setShowAdd(false); load(); }} onSaved={load} />}
    </div>
  );
}
